import { Transition } from '@headlessui/react'
import { DocumentDuplicateIcon, MinusCircleIcon, PlusCircleIcon, Square2StackIcon } from '@heroicons/react/24/outline'
import { Row } from '@tanstack/react-table'
import { Fragment, useEffect, useState } from 'react'
import { IoCloseOutline as CloseIcon } from 'react-icons/io5'
import { useShallow } from 'zustand/react/shallow'

import { Button, Menu } from '@ui/components/core'
import { VerticalMenuIcon } from '@ui/components/icons'
import { DatableLog, DatableTrace, KeyValue } from '@ui/interfaces'
import {
  getAllKeyValuePairs,
  getQueryStatement,
  KeyArrayValuePair,
  parseValue,
} from '@ui/lib/helpers/telemetryQueryParser'
import classNames from '@ui/lib/utils/classNames'
import { useTelemetryStore } from '@ui/stores/explore'

interface DetailsModalProps {
  dataSource: string
  show: boolean
  rowsLength: number
  row: Row<KeyValue> | null
  setModalRow: (index: number) => void
  setHighlightedRow: (index: number | null) => void
  closeModal: () => void
}

export const DetailsModal = ({
  dataSource,
  show,
  rowsLength,
  row,
  setModalRow,
  setHighlightedRow,
  closeModal,
}: DetailsModalProps) => {
  // const { navigateTo } = useAppNavigation()
  const [modalCurrentTab, setModalCurrentTab] = useState<string>('All')
  // const [showTransformList, setShowTransformList] = useState<boolean>(false)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  const { whereClause, setWhereClause, setSelectedEvent } = useTelemetryStore(
    useShallow((state) => ({
      whereClause: state.whereClause,
      setWhereClause: state.setWhereClause,
      setSelectedEvent: state.setSelectedEvent,
    }))
  )

  // const { currentNodeId, setDisplayModal } = useNodeAndEdgeStore(
  //   useShallow((state) => ({
  //     currentNodeId: state.currentNodeId,
  //     setDisplayModal: state.setDisplayModal,
  //   }))
  // )

  // const { selectedPipeline } = usePipelinesStore(
  //   useShallow((state) => ({
  //     selectedPipeline: state.selectedPipeline,
  //   }))
  // )

  const modalTabs: string[] = ['All', 'Priority', 'Resource', 'Attributes']

  useEffect(() => {
    if (row !== null) setSelectedEvent(row.original.record)
  }, [row, setSelectedEvent])

  // useEffect(() => {
  // setShowTransformList(false)
  // }, [closeModal])

  const loadRowIntoModal = (index: number) => {
    setModalRow(index)
    setModalCurrentTab('All')
  }

  const getPriorityFromRecord = (record: DatableLog | DatableTrace) => {
    if (!record) return {}
    // Deep copy the record to avoid mutating the original
    const filteredRecord = JSON.parse(JSON.stringify(record))
    delete filteredRecord.resource
    delete filteredRecord.attributes
    delete filteredRecord.body
    return filteredRecord
  }

  const getDropdownMenuItems = (keyValuePair: KeyArrayValuePair) => {
    return [
      // Note: Leaving in as a reminder that we can generate the code for the transformation based
      // on the selected attribute. This is a future feature.
      // {
      //   label: 'Create transformation',
      //   onClick: () => openTransformationModal(keyValuePair.keys.join('.')),
      //   icon: <BeakerIcon className={'mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500'} />,
      // },
      {
        label: 'Copy value',
        onClick: () => navigator.clipboard.writeText(parseValue(keyValuePair.value)),
        icon: <DocumentDuplicateIcon className={'mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500'} />,
      },
      {
        label: 'Add to query',
        onClick: () => addToQuery(keyValuePair),
        icon: (
          <PlusCircleIcon
            className={'mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500'}
            data-testid="dropdown-add-to-query"
          />
        ),
      },
      {
        label: 'Exclude from query',
        onClick: () => excludeFromQuery(keyValuePair),
        icon: <MinusCircleIcon className={'mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500'} />,
      },
    ]
  }

  const addToQuery = (keyArrayValuePair: KeyArrayValuePair) => {
    whereClause
      ? setWhereClause(`${whereClause} AND ${getQueryStatement(keyArrayValuePair)}`)
      : setWhereClause(`${getQueryStatement(keyArrayValuePair)}`)
    setHighlightedRow(null)
  }

  const excludeFromQuery = (keyArrayValuePair: KeyArrayValuePair) => {
    whereClause
      ? setWhereClause(`${whereClause} AND -${getQueryStatement(keyArrayValuePair)}`)
      : setWhereClause(`-${getQueryStatement(keyArrayValuePair)}`)
  }

  const toggleLineClamp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, lineCount: number) => {
    const element = e.target as Element
    element.classList.toggle('line-clamp-' + lineCount)
    element.classList.toggle('line-clamp-none')
  }

  const parseTimestamp = (timestamp: string) => {
    const timeWithoutMilliseconds = new Date(parseFloat(timestamp) / 1000000)
      .toLocaleTimeString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
      })
      .replace(',', '')
    const milliseconds = new Date(parseFloat(timestamp) / 1000000).getMilliseconds()
    return `${timeWithoutMilliseconds}:${milliseconds}`
  }

  const tabContentsVisible = (tab: string) => {
    return ['All', tab].includes(modalCurrentTab)
  }

  const getTabContents = (tabName: string) => {
    if (row == null) return <></>
    let dataElement
    let objWithParentKey
    switch (tabName) {
      case 'Priority':
        dataElement = getPriorityFromRecord(row.original.record)
        objWithParentKey = { ...dataElement }
        break
      case 'Resource':
        dataElement = row.original.record.resource
        objWithParentKey = { resource: { ...dataElement } }
        break
      case 'Attributes':
        dataElement = row.original.record.attributes
        objWithParentKey = { attributes: { ...dataElement } }
        break
    }

    if (tabContentsVisible(tabName) && dataNotEmpty(dataElement)) {
      return (
        <>
          <div className={'sticky top-0 z-0 border-y bg-gray-100 px-6 py-2 font-medium'}>{tabName}</div>

          {Object.entries(getAllKeyValuePairs(objWithParentKey)).map(([index, keyArrayValuePair]) => {
            return getRecordListing(keyArrayValuePair, index)
          })}
        </>
      )
    } else {
      return <></>
    }
  }

  const dataNotEmpty = (dataElement: unknown) => {
    return JSON.stringify(dataElement) !== '{}' && typeof dataElement === 'object'
  }

  const getRecordListing = (keyArrayValuePair: KeyArrayValuePair, index: string) => {
    return (
      <div key={index} className={'group relative border-b px-6 py-2 text-base'}>
        <div className={'mb-1 grid grid-cols-12 gap-1'}>
          <div className={'col-span-11 row-start-1 font-medium'}>{keyArrayValuePair.keys.join('.')}</div>
          <div className={'relative row-span-2 flex items-center justify-center'}>
            {openMenuId === index && (
              <Menu menuItems={getDropdownMenuItems(keyArrayValuePair)} setOpen={() => setOpenMenuId(index)} />
            )}
          </div>
          <div className={'col-span-10 line-clamp-2'} onClick={(e) => toggleLineClamp(e, 2)}>
            {parseValue(keyArrayValuePair.value)}
          </div>
        </div>
        <VerticalMenuIcon
          className="absolute right-2 top-3 cursor-pointer fill-gray-900 stroke-gray-900 transition-colors hover:fill-gray-500 hover:stroke-gray-500"
          onClick={() => setOpenMenuId(index)}
        />
      </div>
    )
  }

  return (
    <Transition
      as={Fragment}
      show={show}
      enter="transform transition ease-in-out duration-500 sm:duration-700"
      enterFrom="translate-x-full"
      enterTo="translate-x-0"
      leave="transform transition ease-in-out duration-500 sm:duration-700"
      leaveFrom="translate-x-0"
      leaveTo="translate-x-full">
      <div
        className="fixed inset-y-0 right-0 z-[100] h-full w-1/3 border-l border-gray-300 bg-white shadow-lg"
        data-testid="telemetry-details">
        <CloseIcon
          className="absolute right-4 top-4 h-6 w-6 cursor-pointer"
          onClick={closeModal}
          data-testid="telemetry-details-close"
        />
        {row && (
          <div className={'bottom-[400px] flex h-screen flex-col'}>
            <div className="mx-6 mb-1 mt-5 flex font-medium">
              <div className="mr-5 text-2xl text-black">
                {dataSource.slice(0, -1).slice(0, 1).toUpperCase() + dataSource.slice(0, -1).slice(1)}
              </div>
              <div
                className={'mt-1 flex cursor-pointer text-primary-500'}
                onClick={() => {
                  if (row !== null) navigator.clipboard.writeText(JSON.stringify(row.original.record, null, 2))
                }}>
                <div className={'mr-1 mt-1'}>
                  <Square2StackIcon className="h-4 w-auto" />
                </div>
                <span>Copy</span>
              </div>
            </div>
            <div className={'flex items-center justify-between'}>
              {row.original.record.severity_text && (
                <div className={'mx-6 my-2 w-fit rounded-lg bg-blue-500/30 px-2 text-sm font-medium text-blue-700'}>
                  {row.original.record.severity_text}
                </div>
              )}
              {!row.original.record.severity_text && row.original.record.timestamp_nano && (
                <div>
                  <div className={'mx-6 mb-4 mt-2 text-sm font-light text-gray-700'}>
                    {parseTimestamp(row.original.record.timestamp_nano)}
                  </div>
                </div>
              )}
              {!row.original.severity_text && !row.original.timestamp_nano && <div></div>}
              {/* EVENT TRANSFORM */}
              {/* <div className="relative flex flex-row">
                {currentNodeId && (
                  <Button
                    onClick={() => {
                      setDisplayModal(PanelEnum.CODE)
                      navigateTo(RouteNamesEnum.EXISTING_PIPELINE_NODE)
                    }}>
                    Edit Node
                  </Button>
                )}

                {selectedPipeline && selectedPipeline.id && (
                  <Button
                    onClick={() => {
                      setShowTransformList(true)
                    }}>
                    Create New Node
                  </Button>
                )}
                {showTransformList && <NodeTransformList currentNodeId={currentNodeId} show={showTransformList} />}
              </div> */}
            </div>
            {row.original.record.timestamp_nano && row.original.record.severity_text && (
              <div className={'flex'}>
                <div className={'mx-6 mb-4 mt-2 text-sm font-light text-gray-700'}>
                  {parseTimestamp(row.original.record.timestamp_nano)}
                </div>
              </div>
            )}

            {(row.original.record.body || row.original.record.message) && (
              <div className={'mx-0 mb-5 flex min-h-[1em] w-full flex-col border-y bg-gray-200/50 p-6'}>
                <div className={'font-medium'}>{row.original.record.body ? 'body' : 'message'}</div>
                <div className={'line-clamp-4'} onClick={(e) => toggleLineClamp(e, 4)}>
                  {row.original.record.body || row.original.record.message}
                </div>
              </div>
            )}

            <div className={'flex'}>
              <div className="sm:hidden">
                <label htmlFor="tabs" className="sr-only">
                  Select a tab
                </label>
                <select
                  id="tabs"
                  name="tabs"
                  className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-200 focus:outline-none focus:ring-primary-200 sm:text-sm"
                  defaultValue={modalCurrentTab}>
                  {modalTabs.map((tab) => (
                    <option key={tab}>{tab}</option>
                  ))}
                </select>
              </div>
              <div className="hidden sm:block">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex justify-normal" aria-label="Tabs">
                    {modalTabs.map((tab) => (
                      <a
                        key={tab}
                        onClick={() => setModalCurrentTab(tab)}
                        className={classNames(
                          tab === modalCurrentTab
                            ? 'border-primary-500 text-primary-500'
                            : 'cursor-pointer border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'pl-5whitespace-nowrap border-b-2 p-1 text-sm font-medium'
                        )}
                        aria-current={tab ? 'page' : undefined}>
                        {tab}
                      </a>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            <div className={'h-full w-full flex-1 overflow-y-scroll'}>
              <div className={'mb-16 w-full break-all'}>
                {getTabContents('Priority')}
                {getTabContents('Resource')}
                {getTabContents('Attributes')}
              </div>
            </div>

            <div className={'absolute bottom-0 flex w-full flex-row justify-between border-t bg-slate-50 px-5 py-3'}>
              <Button
                variant={'secondary'}
                disabled={row.index === 0}
                onClick={() => {
                  if (row !== null) loadRowIntoModal(row.index - 1)
                }}>
                Previous
              </Button>
              <Button
                variant={'secondary'}
                disabled={row.index === rowsLength - 1}
                onClick={() => {
                  if (row !== null) loadRowIntoModal(row.index + 1)
                }}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Transition>
  )
}
